import type { AssortmentProductRegulationType, AssortmentProductRole } from '@repo/db';
import { LOCALE_FIN } from './i18n';
import { NosVariantRule } from '@repo/db/drizzle';

export const testEnvOffset = 5;
export const sidebarWidth = 240;
export const navbarHeightRaw = 64;
export const navbarHeight = `${navbarHeightRaw}px`;
export const navbarHeightTestEnvRaw = navbarHeightRaw + testEnvOffset;
export const navbarHeightTestEnv = `${navbarHeightTestEnvRaw}px`;

export const BASE_URLS = {
  root: '/',
  pim: '/pim',
  'product-management': '/pim',
  campaigns: '/kampanjat',
  'campaign-tool': '/kampanjat',
  valikoimat: '/valikoimat',
  salesOrders: '/valikoimat/tilaus',
  'image-search': '/kuvahaku',
  'product-assortments': '/valikoimat',
  maintenances: '/huoltopalvelu',
  maintenanceBreak: '/kayttokatko',
  supplementOrders: '/taydennystilaukset',
  'supplement-orders': '/taydennystilaukset',
  styleguide: '/_developer_styleguide',
  settings: '/asetukset',
  sandbox: '/_developer_sandbox',
  error: '/virhe',
  'bike-leasing': '/tyosuhdepyorat',
};

/**
 * Services only available to some chains
 */
export const DISABLED_SERVICES_BY_CHAIN: Record<Chain, string[]> = {
  intersport: [],
  budgetsport: ['supplement-orders'],
};

/**
 *These services are special cases where you don't want to be redirected to, because there's no way back via navigation
 */
export const REDIRECT_EXCLUDED_SERVICES = ['settings', 'system-management'];

export const SERVICE_NAMES_LOCALIZED = {
  pim: LOCALE_FIN.PIM,
  'product-management': LOCALE_FIN.PIM,
  campaigns: LOCALE_FIN.CAMPAIGNS,
  'campaign-tool': LOCALE_FIN.CAMPAIGN_TOOL,
  'product-assortments': LOCALE_FIN.ASSORTMENT_MANAGEMENT,
  'image-search': LOCALE_FIN.IMAGE_SEARCH,
  maintenances: LOCALE_FIN.MAINTENANCES,
  supplementOrders: LOCALE_FIN.SUPPLEMENT_ORDERS,
  'supplement-orders': LOCALE_FIN.SUPPLEMENT_ORDERS,
  settings: LOCALE_FIN.SETTINGS,
  styleguide: 'Styleguide',
  sandbox: 'Sandbox',
  'system-management': LOCALE_FIN.SYSTEM_MANAGEMENT,
  'order-management': LOCALE_FIN.ORDER_MANAGEMENT,
  'print-management': LOCALE_FIN.PRINT_MANAGEMENT,
};

export const MAINTENANCE_BREAK_SERVICES_FOR_ALL_USERS = ['settings'];

export const BOOL_OPTIONS = {
  true: { name: 'Kyllä', value: '1' },
  false: { name: 'Ei', value: '0' },
};

/**
 * Used in MassEditDialog to determine mass edit options
 * Used in ProductsListSidebarFilter, ProductsListTableCellContent, SavedSearchesFilterRow to map values to natural language
 */
export const FILTER_MAPPING = {
  brand: 'brandsMapped',
  campaign: 'campaignsMapped_new',
  category: 'categoriesMapped',
  // delivery: 'deliveryMethodsMapped', Not in app data. Only reference in image-search where it seems that it's not actually used.
  imgType: 'imageTypesMapped',
  productgroup: 'categoriesMapped',
  productLabels: 'productLabelsMapped',
  productType: 'productTypesMapped',
  season: 'seasonsMapped',
  sports: 'sportsMapped',
  mainSport: 'sportsMapped',
  subcategory: 'subCategoriesMapped',
  targetgroup: 'targetGroupsMapped',
  technologies: 'technologiesMapped',
  // warranty: 'warrantiesMapped', Not in app data. Only reference in image-search where it seems that it's not actually used.
  // warehouse: 'warehousesMapped', Not in app data. Reference in image-search where it seems that it's not actually used.
  b2bBlocked: 'booleanOptions',
  deliverySalesProduct: 'booleanOptions',
  // priceType: 'priceTypesMapped', Not in app data.
  wholesaleProduct: 'booleanOptions',
  nosVariantRules: 'nosVariantRulesMapped',
  nosAssortments: 'nosAssortmentsMapped',
  newNosProduct: 'newNosProductMapped',
  nosSeasons: 'nosSeasonsMapped',
} as const;

/**
 * Used in FilterRowValueInput to determine filter value options (objects are always sorted A-Z)
 * When using array, custom sorting can be applied in server
 */
export const FILTER_VALUES_MAPPING = {
  ...FILTER_MAPPING,
  campaign: 'campaigns_new',
} as const;

export const savedSearchesTableIds = [
  { id: 'searchName', name: 'Haun nimi' },
  { id: 'filtersCount', name: 'Hakuehdot lkm' },
  { id: 'searchOperator', name: 'Hakuehtojen toteutuminen' },
  { id: 'createdAt', name: 'Tallennettu pvm' },
  { id: 'updatedAt', name: 'Muokattu viimeksi' },
];

export const savedProductListColumnPresetsTableIds = [
  { id: 'presetName', name: 'Mallin nimi' },
  { id: 'columnsCount', name: 'Sarakkeet lkm' },
  { id: 'createdAt', name: 'Tallennettu pvm' },
  { id: 'updatedAt', name: 'Muokattu viimeksi' },
];

export const messagesTableIds = [
  { id: 'userName', name: 'Viestin kirjoittaja' },
  { id: 'sapId', name: 'Viestikohteen SAP-nro' },
  {
    id: 'product',
    name: 'Viestiin liittyvä brändi, tuote ja ensisijainen kuva',
  },
  { id: 'message', name: 'Viesti' },
  { id: 'createdAt', name: 'Viesti kirjoitettu' },
];

export const PRODUCT_BASE_URL = 'https://www.intersport.fi/fi/tuote/';

export const CSVSeparatorsMapped = {
  comma: { name: 'Pilkku (,)', value: 'comma', rawValue: ',' },
  'semi-colon': { name: 'Puolipiste (;)', value: 'semi-colon', rawValue: ';' },
  tab: { name: 'Tab ( )', value: 'tab', rawValue: '\t' },
};

export type CSVSeparator = keyof typeof CSVSeparatorsMapped;

export const reportExportFileTypesMapped = {
  xlsx: {
    name: '.xlsx',
    value: 'xlsx',
    rawValue: 'xlsx',
  },
  csv: {
    name: '.csv',
    value: 'csv',
    rawValue: 'csv',
  },
};

export const IMAGES = {
  intersportLogo:
    'https://studio.intersport.fi/pub/web/ifi-services/admin/media/intersport_logo.svg',
  budgetsportLogo: 'https://studio.intersport.fi/pub/web/ifi-services/admin/media/budget_logo.svg',
};

export const DATETIME_TABLE_DATA_IDS = [
  'updated',
  'saleStarts',
  'saleEnds',
  'newUntil',
  'onlyinwebUntil',
  'ovhPriceEnds',
  'enableSellingTime',
  'activeFromTime',
] as const;
export const DATE_TABLE_DATA_IDS = [
  'last_arrived_dcu1_date',
  'last_arrived_abakus_date',
  'last_putaway_dcu1_date',
  'last_putaway_abakus_date',
  'last_putaway_dcu2_date',
  'last_arrived',
  'last_putaway',
] as const;
export const RICH_TABLE_DATA_IDS = [
  'category',
  // 'delivery',
  'productLabels',
  'productType',
  'sports',
  'mainSport',
  'subcategory',
  'targetgroup',
  'technologies',
  // 'warehouse',
  // 'warranty',
  // 'priceType',
] as const;
export const SANITIZE_TABLE_DATA_IDS = [
  'printDescription',
  'description',
  'technicalInformation',
] as const;
export const PRICE_TABLE_DATA_IDS = ['price', 'campaignPrice', 'specialPrice'] as const;

export const IMAGE_DATA_IDS = ['images', 'image'];

export const BASE_URL =
  process.env.SW_REACT_APP_ENV === 'production'
    ? 'https://ifi.intersport.fi/'
    : 'https://ifiservices-admin.stage.kes.crasman.cloud/';

export const PRODUCT_IMAGES_DOWNLOAD_URL = `${BASE_URL}api/tuote-lataa-kuvat/`;
export const CAMPAIGN_IMAGES_DOWNLOAD_URL = `${BASE_URL}api/kampanja-lataa-kuvat-uusi/`;
export const CAMPAIGN_IMAGES_DOWNLOAD_URL_OLD = `${BASE_URL}api/kampanja-lataa-kuvat/`;
export const CAMPAIGN_TEXT_DOWNLOAD_URL = `${BASE_URL}api/kampanja-lataa-tekstit-uusi/`;
export const CAMPAIGN_TEXT_DOWNLOAD_URL_OLD = `${BASE_URL}api/kampanja-lataa-tekstit/`;
export const IMAGES_DOWNLOAD_URL = `${BASE_URL}api/lataa-kuvat/`;

export const YES_NO_OPTIONS = {
  1: { name: 'Kyllä', value: '1' },
  0: { name: 'Ei', value: '0' },
};

export const DISCOUNT_TYPE = {
  groupsale: 'Ryhmäalennus',
  single: 'Tuotekohtainen alennus',
  campaign: 'Kampanja-alennus',
  last_chance: 'Poistomyyntihinnoittelu',
  ovh: 'ovh. hinnoittelu',
  campaign_bs: 'Kampanja-alennus',
  rob: 'Poistomyyntihinnoittelu',
  web_sale_bs: 'Verkon lisäale',
  sk_ovh: 'Seurakauppa OVH',
  sk_teamshop: 'Seurahinta',
};

// Note: this is used for the table headers, but not the body columns, with some exceptions.
// Check src/app-pim/Pricing/ProductPricingTableRow.jsx for the body columns.
export const PRICING_TABLE_HEADERS = [
  {
    id: 'discountType',
    name: 'Hinnoittelutyyppi',
    sorting: false,
    views: ['WEB_PRICINGS', 'WEB_PRICINGS_LEGACY', 'STORE_PRICINGS', 'SK_PRICINGS'],
  },
  {
    id: 'campaign',
    name: 'Kampanja',
    sorting: false,
    views: ['WEB_PRICINGS', 'WEB_PRICINGS_LEGACY', 'STORE_PRICINGS'],
  },
  {
    id: 'discountPercentage',
    name: 'Alennus %',
    sorting: false,
    views: ['WEB_PRICINGS', 'WEB_PRICINGS_LEGACY', 'STORE_PRICINGS', 'SK_PRICINGS'],
  },
  {
    id: 'price',
    name: 'Hinta',
    sorting: false,
    views: ['WEB_PRICINGS', 'WEB_PRICINGS_LEGACY', 'STORE_PRICINGS', 'SK_PRICINGS'],
  },
  {
    id: 'margin',
    name: 'Kate',
    sorting: false,
    views: ['WEB_PRICINGS', 'WEB_PRICINGS_LEGACY', 'STORE_PRICINGS', 'SK_PRICINGS'],
  },
  {
    id: 'starts',
    name: 'Alkaa',
    sorting: true,
    views: ['WEB_PRICINGS', 'WEB_PRICINGS_LEGACY', 'STORE_PRICINGS', 'SK_PRICINGS'],
  },
  {
    id: 'duration',
    name: 'Pv jäljellä',
    sorting: false,
    views: ['WEB_PRICINGS', 'WEB_PRICINGS_LEGACY', 'STORE_PRICINGS'],
  },
  {
    id: 'ends',
    name: 'Päättyy',
    sorting: true,
    views: ['WEB_PRICINGS', 'WEB_PRICINGS_LEGACY', 'STORE_PRICINGS'],
  },
  {
    id: 'visibility',
    name: 'Käytössä',
    sorting: true,
    views: ['WEB_PRICINGS', 'WEB_PRICINGS_LEGACY', 'STORE_PRICINGS'],
  },
  {
    id: 'colors',
    name: 'Värit',
    sorting: false,
    views: ['WEB_PRICINGS', 'WEB_PRICINGS_LEGACY', 'STORE_PRICINGS', 'SK_PRICINGS'],
  },
  {
    id: 'limit',
    name: 'kpl-rajoitus',
    sorting: false,
    views: ['WEB_PRICINGS', 'WEB_PRICINGS_LEGACY'],
  },
  {
    id: 'sellBeforeDiscount',
    name: 'Myynti sallittu ennen*',
    sorting: false,
    views: ['WEB_PRICINGS', 'WEB_PRICINGS_LEGACY'],
  },
];

export const COMMON_API_ROUTES = {
  APP_DATA: '/api/app-data',
  CAMPAIGNS: '/api/campaigns/',
  CAMPAIGN: '/api/campaign/',
  CAMPAIGN_PRODUCTS: '/api/campaign-products/',
  CAMPAIGN_PRODUCT_COLORS: '/api/campaign-product-colors/',
  CAMPAIGN_PRODUCT_PRICE: '/api/campaign-product-price/',
  CAMPAIGN_PRODUCT_DISCOUNT_MASS_EDIT: '/api/campaign-product-discount-mass-edit/',
  CAMPAIGN_PRICING_DATA: '/api/campaign-pricing-data/',
};

export const CHAINS = ['intersport', 'budgetsport'] as const;
export type Chain = (typeof CHAINS)[number];

export const CHAIN_URLS = {
  intersport: {
    urls: {
      GET_PRODUCTS: '/api/products/?chain=intersport&',
      EDIT_PRODUCTS: '/api/products/?chain=intersport&',
      GET_PRODUCT: '/api/product/?chain=intersport&',
      PUT_PRODUCT: '/api/product/?chain=intersport&',
      POST_PRICING: '/api/product_pricing/?chain=intersport&',
      PUT_PRICING: '/api/product_pricing/?chain=intersport&',
      DELETE_PRICING: '/api/product_pricing/?chain=intersport&',
      GET_SAVED_SEARCHES: '/api/saved_searches/?chain=intersport&',
      POST_SAVED_SEARCH: '/api/saved_searches/?chain=intersport&',
      PUT_SAVED_SEARCH: '/api/saved_searches/?chain=intersport&',
      DELETE_SAVED_SEARCH: '/api/saved_searches/?chain=intersport&',
      GET_PRODUCT_PAGE_DATA: '/api/product_page_data/?chain=intersport&',
      GET_SHOP_DATA: '/api/shop_data/?chain=intersport&',
      GET_MAINTENANCE_SLOTS: '/api/maintenance_slots/?chain=intersport&',
      PUT_MAINTENANCE_SLOTS: '/api/maintenance_slots/?chain=intersport&',
      UPLOAD_FILE: '/api/upload_file/',
      GET_PRODUCT_MESSAGES: '/api/product_messages/?chain=intersport&',
      POST_PRODUCT_MESSAGE: '/api/product_messages/?chain=intersport&',
      DELETE_PRODUCT_MESSAGE: '/api/product_messages/?chain=intersport&',
      PUT_MESSAGES_READ: '/api/product_messages/read/?chain=intersport&',
      PUT_MESSAGES_UNREAD: '/api/product_messages/unread/?chain=intersport&',
      GET_NEW_CREDENTIALS: '/api/new_credentials/',
      PASSWORD_FORGOT: '/api/password_forgot/',
      PASSWORD_RESET: '/api/password_reset/',
      GET_CAMPAIGNS: '/api/intersport/campaigns/?chain=intersport&',
      PUT_PRICING_COMPARISON: '/api/intersport/price_comparison/?chain=intersport&',
      PUT_PRODUCT_MEDIA: '/api/product_media/',
      COPY_PRODUCT_MEDIA: '/api/media_copy_route/',
      QUICK_SEARCH: '/api/quick_search/?chain=intersport&',
      GET_SAVED_PRODUCT_LIST_COLUMN_PRESETS:
        '/api/saved_product_list_column_presets/?chain=intersport&',
      POST_SAVED_PRODUCT_LIST_COLUMN_PRESETS:
        '/api/saved_product_list_column_presets/?chain=intersport&',
      PUT_SAVED_PRODUCT_LIST_COLUMN_PRESETS:
        '/api/saved_product_list_column_presets/?chain=intersport&',
      DELETE_SAVED_PRODUCT_LIST_COLUMN_PRESETS:
        '/api/saved_product_list_column_presets/?chain=intersport&',
      GET_SUPPLEMENT_ORDERS: '/api/supplement-orders/?chain=intersport&',
      GET_SUPPLEMENT_ORDER: '/api/supplement-order/?chain=intersport&',
      POST_SUPPLEMENT_ORDER: '/api/supplement-order/?chain=intersport&',
    },
  },
  budgetsport: {
    urls: {
      GET_PRODUCTS: '/api/budgetsport/products/?chain=budgetsport&',
      EDIT_PRODUCTS: '/api/budgetsport/products/?chain=budgetsport&',
      GET_PRODUCT: '/api/budgetsport/product/?chain=budgetsport&',
      PUT_PRODUCT: '/api/budgetsport/product/?chain=budgetsport&',
      POST_PRICING: '/api/product_pricing/?chain=budgetsport&',
      PUT_PRICING: '/api/product_pricing/?chain=budgetsport&',
      DELETE_PRICING: '/api/product_pricing/?chain=budgetsport&',
      GET_SAVED_SEARCHES: '/api/saved_searches/?chain=budgetsport&',
      POST_SAVED_SEARCH: '/api/saved_searches/?chain=budgetsport&',
      PUT_SAVED_SEARCH: '/api/saved_searches/?chain=budgetsport&',
      DELETE_SAVED_SEARCH: '/api/saved_searches/?chain=budgetsport&',
      GET_PRODUCT_PAGE_DATA: '/api/budgetsport/product_page_data/?chain=budgetsport&',
      GET_SHOP_DATA: '/api/budgetsport/shop_data/?chain=budgetsport&',
      GET_MAINTENANCE_SLOTS: '/api/maintenance_slots/?chain=budgetsport&',
      PUT_MAINTENANCE_SLOTS: '/api/maintenance_slots/?chain=budgetsport&',
      UPLOAD_FILE: '/api/upload_file/',
      GET_PRODUCT_MESSAGES: '/api/product_messages/?chain=budgetsport&',
      POST_PRODUCT_MESSAGE: '/api/product_messages/?chain=budgetsport&',
      DELETE_PRODUCT_MESSAGE: '/api/product_messages/?chain=budgetsport&',
      PUT_MESSAGES_READ: '/api/product_messages/read/?chain=budgetsport&',
      PUT_MESSAGES_UNREAD: '/api/product_messages/unread/?chain=budgetsport&',
      GET_NEW_CREDENTIALS: '/api/new_credentials/',
      PASSWORD_FORGOT: '/api/password_forgot/',
      PASSWORD_RESET: '/api/password_reset/',
      GET_CAMPAIGNS: '/api/budgetsport/campaigns/?chain=budgetsport&',
      PUT_PRODUCT_MEDIA: '/api/product_media/',
      COPY_PRODUCT_MEDIA: '/api/media_copy_route/',
      QUICK_SEARCH: '/api/budgetsport/quick_search/?chain=budgetsport&',
      GET_SAVED_PRODUCT_LIST_COLUMN_PRESETS:
        '/api/saved_product_list_column_presets/?chain=budgetsport&',
      POST_SAVED_PRODUCT_LIST_COLUMN_PRESETS:
        '/api/saved_product_list_column_presets/?chain=budgetsport&',
      PUT_SAVED_PRODUCT_LIST_COLUMN_PRESETS:
        '/api/saved_product_list_column_presets/?chain=budgetsport&',
      DELETE_SAVED_PRODUCT_LIST_COLUMN_PRESETS:
        '/api/saved_product_list_column_presets/?chain=budgetsport&',
    },
  },
} satisfies Record<Chain, { urls: Record<string, string> }>;

const INITIAL_PRODUCT_BALANCES_COLUMNS = [
  { id: 'size' },
  { id: 'ean' },
  { id: 'arrived' },
  { id: 'putaway' },
  { id: 'warehouses_web_total' },
  { id: 'warehouses_total' },
];
const INTER_INTIAL_PRODUCT_BALANCES_COLUMNS = [
  { id: 'size' },
  { id: 'ean' },
  { id: 'arrived' },
  { id: 'putaway' },
  { id: 'warehouse_abakus' },
  { id: 'warehouse_oulu-joutsensilta' },
  { id: 'warehouse_helsinki-forum' },
  { id: 'warehouse_huntteri' },
  { id: 'warehouse_halti' },
  { id: 'warehouses_web_total' },
  { id: 'warehouses_total' },
];
const BUDGET_INITIAL_PRODUCT_BALANCES_COLUMNS = [
  { id: 'size' },
  { id: 'ean' },
  { id: 'putaway' },
  { id: 'warehouses_web_total' },
  { id: 'warehouses_total' },
  { id: 'warehouse_DCU2' },
  { id: 'warehouse_0134' },
  { id: 'warehouse_0393' },
  { id: 'warehouse_0652' },
  { id: 'warehouse_1127' },
  { id: 'warehouse_1169' },
  { id: 'warehouse_1194' },
  { id: 'warehouse_1350' },
  { id: 'warehouse_1574' },
  { id: 'warehouse_1605' },
];

export function getInitialProductBalancesColumns(activeChain: 'intersport' | 'budgetsport') {
  return activeChain === 'intersport'
    ? INTER_INTIAL_PRODUCT_BALANCES_COLUMNS
    : activeChain === 'budgetsport'
      ? BUDGET_INITIAL_PRODUCT_BALANCES_COLUMNS
      : INITIAL_PRODUCT_BALANCES_COLUMNS;
}

export function getInitialCampaignsListColumns() {
  return [
    { id: 'campaignId' },
    { id: 'campaignName' },
    { id: 'campaignProductCount' },
    { id: 'campaignType' },
    { id: 'campaignStarts' },
    { id: 'campaignEnds' },
    { id: 'campaignDays' },
  ];
}
export function getInitialCampaignProductsListColumns() {
  return [
    { id: 'campaignProductImage' },
    { id: 'campaignProductSapId' },
    { id: 'campaignProductKwNumber' },
    { id: 'campaignProductSapProductName' },
    { id: 'campaignProductBrand' },
    { id: 'campaignProductCategory' },
    { id: 'campaignProductWebBalance' },
    { id: 'campaignProductStoresBalance' },
    { id: 'campaignProductSeason' },
    { id: 'campaignProductColors' },
    { id: 'campaignProductOvhPrice' },
    { id: 'campaignProductActivePrice' },
    { id: 'campaignProductMargin' },
    { id: 'campaignProductDiscount' },
    { id: 'campaignProductCampaignPrice' },
  ];
}

export const IMAGE_SEARCH_FILTERS_MAPPED = {
  brand: {
    value: 'brand',
    name: 'Brändi',
    operator: 'equals',
    type: 'multi-choice',
    selectAll: false,
  },
  targetgroup: {
    value: 'targetgroup',
    name: 'Kohderyhmä',
    operator: 'equals',
    type: 'multi-choice',
  },
  season: {
    value: 'season',
    name: 'Sesonki',
    operator: 'equals',
    type: 'multi-choice',
  },
  sports: {
    value: 'sports',
    name: 'Laji',
    operator: 'equals',
    type: 'multi-choice',
  },
  imgType: {
    value: 'imgType',
    name: 'Kuvatyyppi',
    operator: 'equals',
    type: 'multi-choice',
  },
  imgWidth: {
    value: 'imgWidth',
    name: 'Kuvan leveys',
    operator: 'greater-equal',
    type: 'text',
  },
  imgHeight: {
    value: 'imgHeight',
    name: 'Kuvan korkeus',
    operator: 'greater-equal',
    type: 'text',
  },
  sapId: {
    value: 'sapId',
    name: 'SAP-nro',
    operator: 'includes',
    type: 'text',
  },
  kwNumber: {
    value: 'kwNumber',
    name: 'KW-nro',
    operator: 'includes',
    type: 'text',
  },
  productName: {
    value: 'productName',
    name: 'Tuotenimi',
    operator: 'includes',
    type: 'text',
  },
};

export const NOS_PRODUCT_SEARCH_FILTERS = {
  nosSeasons: {
    value: 'nosSeasons',
    name: 'Sesonki',
    operator: 'equals',
    type: 'select',
  },
  brand: {
    value: 'brand',
    name: 'Brändi',
    operator: 'equals',
    type: 'multi-choice',
    selectAll: false,
  },
  productType: {
    value: 'productType',
    name: 'Tuotetyyppi',
    operator: 'equals',
    type: 'multi-choice',
  },
  nosAssortments: {
    value: 'nosAssortments',
    name: 'NOS-Valikoima',
    operator: 'equals',
    type: 'multi-choice',
  },
  /*nosBlockedVariants: {
    value: 'nosBlockedVariants',
    name: 'Täydennystilauksilta estetty variantteja',
    operator: 'equals',
    type: 'multi-choice',
  },*/
  nosVariantRules: {
    value: 'nosVariantRules',
    name: 'Tilaussääntö',
    operator: 'equals',
    type: 'multi-choice',
    selectAll: false,
  },
  newNosProduct: {
    value: 'newNosProduct',
    name: 'Uudet tuotteet ja poiminnat',
    operator: 'equals',
    type: 'multi-choice',
    selectAll: false,
  },
  sapName: {
    value: 'sapName',
    name: 'Tuotenimi (SAP)',
    operator: 'includes',
    type: 'text',
  },
  sapId: {
    value: 'sapId',
    name: 'SAP-nro',
    operator: 'includes',
    type: 'text',
  },
  kwNumber: {
    value: 'kwNumber',
    name: 'KW-nro',
    operator: 'includes',
    type: 'text',
  },

  /*
  category: {
    value: 'category',
    name: 'Kategoria',
    operator: 'equals',
    type: 'multi-choice',
  },
  subcategory: {
    value: 'subcategory',
    name: 'Alakategoria',
    operator: 'equals',
    type: 'multi-choice',
  },
  sports: {
    value: 'sports',
    name: 'Laji',
    operator: 'equals',
    type: 'multi-choice',
  }, */

  /*
  wholesaleProduct: {
    value: 'wholesaleProduct',
    name: 'Seurakauppa',
    operator: 'equals',
    type: 'multi-choice',
  }, */
};

export const PRODUCT_SEARCH_FILTERS = {
  sapName: {
    value: 'sapName',
    name: 'Tuotenimi (SAP)',
    operator: 'includes',
    type: 'text',
  },
  kwNumber: {
    value: 'kwNumber',
    name: 'KW-nro',
    operator: 'includes',
    type: 'text',
  },
  brand: {
    value: 'brand',
    name: 'Brändi',
    operator: 'equals',
    type: 'multi-choice',
    selectAll: false,
  },
  productType: {
    value: 'productType',
    name: 'Tuotetyyppi',
    operator: 'equals',
    type: 'multi-choice',
  },
  category: {
    value: 'category',
    name: 'Kategoria',
    operator: 'equals',
    type: 'multi-choice',
  },
  subcategory: {
    value: 'subcategory',
    name: 'Alakategoria',
    operator: 'equals',
    type: 'multi-choice',
  },
  sports: {
    value: 'sports',
    name: 'Laji',
    operator: 'equals',
    type: 'multi-choice',
  },
  season: {
    value: 'season',
    name: 'Sesonki',
    operator: 'equals',
    type: 'multi-choice',
  },
  sapId: {
    value: 'sapId',
    name: 'SAP-nro',
    operator: 'includes',
    type: 'text',
  },
  wholesaleProduct: {
    value: 'wholesaleProduct',
    name: 'Seurakauppa',
    operator: 'equals',
    type: 'multi-choice',
  },
};

export const PRODUCT_TYPES_MAPPED = {
  clothing: LOCALE_FIN.CLOTHING,
  shoe: LOCALE_FIN.SHOES,
  equipment: LOCALE_FIN.EQUIPMENT,
};

export const ASSORTMENT_TYPES_MAPPED = {
  clothing: LOCALE_FIN.CLOTHING,
  shoe: LOCALE_FIN.SHOES,
  equipment: LOCALE_FIN.EQUIPMENT,
  food: LOCALE_FIN.FOOD,
  multi: LOCALE_FIN.MULTI_PRODUCT_TYPE,
};

export const TARGETGROUPS_MAPPED = {
  men: LOCALE_FIN.targetgroup_men,
  women: LOCALE_FIN.targetgroup_women,
  children: LOCALE_FIN.targetgroup_children,
  boys: LOCALE_FIN.targetgroup_boys,
  girls: LOCALE_FIN.targetgroup_girls,
  youth: LOCALE_FIN.targetgroup_youth,
};

export const ASSORTMENT_PRODUCT_REGULATION_LINKS = {
  CBAM: 'https://tulli.fi/hiilirajamekanismi',
  DEFORESTATION: 'https://www.europarl.europa.eu/doceo/document/TA-9-2022-0311_FI.html',
} satisfies Record<AssortmentProductRegulationType, string>;

export const TEST_PRODUCT_SAP_IDS = ['56496953', '56497348', '56505386'];

export const ORDER_GROUP_TYPES = [
  {
    name: LOCALE_FIN.PREORDER,
    value: 'SALES_ORDER',
  },
  {
    name: LOCALE_FIN.NOS_ORDER,
    value: 'NOS_ORDER',
  },
];

export const ORDER_TYPES = [
  {
    name: LOCALE_FIN.SALES_ORDER,
    value: 'SALES_ORDER',
  },
  {
    name: LOCALE_FIN.PURCHASE_ORDER,
    value: 'PURCHASE_ORDER',
  },
];

export const ADMIN_ORDER_DEADLINE_FILTERS = [
  {
    name: 'Vuorokauden sisällä',
    value: 'DAY',
  },
  {
    name: 'Alle viikko sitten',
    value: 'WEEK',
  },
  {
    name: 'Alle kuukausi sitten',
    value: 'MONTH',
  },
  {
    name: 'Alle vuosi sitten',
    value: 'YEAR',
  },
];

export const AssortmentProductRoleOptions: Record<
  string,
  { name: string; value: AssortmentProductRole }[]
> = {
  intersport: [
    {
      name: LOCALE_FIN.ROLE_TIER1,
      value: 'TIER1',
    },
    {
      name: LOCALE_FIN.ROLE_TIER2,
      value: 'TIER2',
    },
    {
      name: LOCALE_FIN.ROLE_TIER3,
      value: 'TIER3',
    },
  ],
  budgetsport: [
    {
      name: LOCALE_FIN.ROLE_MEGA,
      value: 'MEGA',
    },
    {
      name: LOCALE_FIN.ROLE_MUST_WIN_SPRING_SUMMER,
      value: 'MUST_WIN_SPRING_SUMMER',
    },
    {
      name: LOCALE_FIN.ROLE_MUST_WIN_AUTUMN,
      value: 'MUST_WIN_AUTUMN',
    },
    {
      name: LOCALE_FIN.ROLE_MUST_WIN_WINTER,
      value: 'MUST_WIN_WINTER',
    },
    {
      name: LOCALE_FIN.ROLE_MUST_WIN_OTHER,
      value: 'MUST_WIN_OTHER',
    },
    {
      name: LOCALE_FIN.ROLE_BATCH,
      value: 'BATCH',
    },
    {
      name: LOCALE_FIN.ROLE_365_POOL,
      value: 'POOL_365',
    },
  ],
};

export const ASSORTMENT_PRODUCT_ROLE_MAPPING = Object.fromEntries(
  Object.values(AssortmentProductRoleOptions)
    .flat()
    .map((option) => [option.value, option.name])
);
